/** Coverage & Sites tab - shows where a supplier can be used (assignment levels: all
 * sites/client account/site group/individual site) plus an "Assign sites" side panel.
 * Demo Mode only, local-only mock data - see `supplierProfileData.ts`. */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, Drawer, MenuItem, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Section } from '../../../../../components/ui/Section';
import {
  getSupplierCoverageAssignments, SupplierCoverageAssignment, SupplierCoverageAssignmentLevel,
} from '../../../../../mocks/data/supplierProfileData';

export interface CoverageSitesTabProps {
  supplierId: number;
}

const ASSIGNMENT_LEVEL_LABEL: Record<SupplierCoverageAssignmentLevel, string> = {
  AllSites: 'All sites',
  ClientAccount: 'Client account',
  SiteGroup: 'Site group',
  IndividualSite: 'Individual site',
};

const CoverageSitesTab: React.FC<CoverageSitesTabProps> = ({ supplierId }) => {
  const { t } = useTranslation('suppliers');
  const [assignments, setAssignments] = useState<SupplierCoverageAssignment[]>(() => getSupplierCoverageAssignments(supplierId));
  const [assignPanelOpen, setAssignPanelOpen] = useState(false);
  const [newLevel, setNewLevel] = useState<SupplierCoverageAssignmentLevel>('IndividualSite');
  const [newTarget, setNewTarget] = useState('');

  useEffect(() => {
    setAssignments(getSupplierCoverageAssignments(supplierId));
  }, [supplierId]);

  const handleAssign = () => {
    if (!newTarget.trim()) return;
    setAssignments((current) => [
      ...current,
      {
        id: Date.now(),
        level: newLevel,
        clientAccount: newLevel === 'ClientAccount' ? newTarget : undefined,
        siteGroup: newLevel === 'SiteGroup' ? newTarget : undefined,
        site: newLevel === 'IndividualSite' ? newTarget : undefined,
        deliveryDays: [],
        orderCutOff: '-',
        leadTimeDays: 0,
        minimumOrderValue: 0,
        status: 'Active',
      },
    ]);
    setNewTarget('');
    setAssignPanelOpen(false);
  };

  return (
    <Section>
      <Box sx={{ p: 4 }}>
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}>
          <Typography variant="textSM"
            fontWeight={600}>{t('coverageAssignments', 'Coverage & site assignments')}</Typography>
          <Button variant="secondary"
            size="md"
            onClick={() => setAssignPanelOpen(true)}>{t('assignSites', 'Assign sites')}</Button>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('assignmentLevel', 'Assignment level')}</TableCell>
              <TableCell>{t('clientAccount', 'Client account')}</TableCell>
              <TableCell>{t('siteGroup', 'Site group')}</TableCell>
              <TableCell>{t('site', 'Site')}</TableCell>
              <TableCell>{t('region', 'Region')}</TableCell>
              <TableCell>{t('deliveryDays', 'Delivery days')}</TableCell>
              <TableCell>{t('orderCutOff', 'Order cut-off')}</TableCell>
              <TableCell>{t('leadTimeDays', 'Lead time (days)')}</TableCell>
              <TableCell>{t('minimumOrderValue', 'Minimum order value')}</TableCell>
              <TableCell>{t('status', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.length === 0 && (
              <TableRow><TableCell colSpan={10}>{t('noCoverageAssignments', 'No coverage assigned yet - this supplier is not inherited to any sites.')}</TableCell></TableRow>
            )}
            {assignments.map((assignment) => (
              <TableRow key={assignment.id}>
                <TableCell>{t(`assignmentLevel${assignment.level}`, ASSIGNMENT_LEVEL_LABEL[assignment.level])}</TableCell>
                <TableCell>{assignment.clientAccount ?? '-'}</TableCell>
                <TableCell>{assignment.siteGroup ?? '-'}</TableCell>
                <TableCell>{assignment.site ?? (t('inheritedFromParent', 'Inherited'))}</TableCell>
                <TableCell>{assignment.region ?? '-'}</TableCell>
                <TableCell>{assignment.deliveryDays.length ? assignment.deliveryDays.join(', ') : '-'}</TableCell>
                <TableCell>{assignment.orderCutOff}</TableCell>
                <TableCell>{assignment.leadTimeDays}</TableCell>
                <TableCell>{assignment.minimumOrderValue}</TableCell>
                <TableCell><Chip label={assignment.status}
                  size="small"
                  color={assignment.status === 'Active' ? 'success' : 'default'} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Drawer anchor="right"
        open={assignPanelOpen}
        onClose={() => setAssignPanelOpen(false)}>
        <Box sx={{ width: 360, p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 3 }}>{t('assignSites', 'Assign sites')}</Typography>
          <Stack spacing={3}>
            <TextField select
              label={t('assignmentLevel', 'Assignment level')}
              value={newLevel}
              onChange={(e) => setNewLevel(e.target.value as SupplierCoverageAssignmentLevel)}
            >
              {(Object.keys(ASSIGNMENT_LEVEL_LABEL) as SupplierCoverageAssignmentLevel[]).map((level) => (
                <MenuItem key={level}
                  value={level}>{t(`assignmentLevel${level}`, ASSIGNMENT_LEVEL_LABEL[level])}</MenuItem>
              ))}
            </TextField>
            {newLevel !== 'AllSites' && (
              <TextField
                label={newLevel === 'ClientAccount' ? t('clientAccount', 'Client account') : newLevel === 'SiteGroup' ? t('siteGroup', 'Site group') : t('site', 'Site')}
                value={newTarget}
                onChange={(e) => setNewTarget(e.target.value)}
              />
            )}
            <Button variant="primary"
              onClick={handleAssign}>{t('assign', 'Assign')}</Button>
          </Stack>
        </Box>
      </Drawer>
    </Section>
  );
};

export default CoverageSitesTab;
